import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { User, Palette, Bell, Link as LinkIcon, CreditCard, Loader2, Check, FileText, HelpCircle, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';

import ProfileTab from './settings/ProfileTab';
import BrandKitTab from './settings/BrandKitTab';
import IntegrationsTab from './settings/IntegrationsTab';
import NotificationsTab from './settings/NotificationsTab';
import BillingTab from './settings/BillingTab';
import LetterheadSettings from './settings/LetterheadSettings';
import HelpToursTab from './settings/HelpToursTab';
import DangerZoneTab from './settings/DangerZoneTab';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SettingsPage = () => {
  const { user, refreshUser } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [profile, setProfile] = useState({ full_name: '', business_name: '', phone: '', industry: '' });
  const [brandKit, setBrandKit] = useState({
    primary_color: '#10B981', secondary_color: '#1F2937', font: 'Inter', brand_voice: '', tone: 'professional',
  });
  const [paymentChecking, setPaymentChecking] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);

  useEffect(() => {
    fetchProfile();
    fetchSubscription();
    fetchBrandKit();
    const sessionId = searchParams.get('session_id');
    if (sessionId) checkPaymentStatus(sessionId);
  }, [searchParams]);

  const fetchProfile = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/settings/profile`);
      setProfile(res.data);
    } catch (error) { console.error('Fetch profile error:', error); }
  };

  const fetchBrandKit = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/settings/brand-kit`);
      const saved = res.data || {};
      setBrandKit(prev => ({
        primary_color: saved.primary_color || prev.primary_color,
        secondary_color: saved.secondary_color || prev.secondary_color,
        font: saved.font || prev.font,
        brand_voice: saved.brand_voice ?? prev.brand_voice,
        tone: saved.tone || prev.tone,
      }));
    } catch (error) { console.error('Fetch brand kit error:', error); }
  };

  const fetchSubscription = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/payments/subscription`);
      setSubscription(res.data);
    } catch (error) { console.error('Fetch subscription error:', error); }
  };

  const checkPaymentStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 5) { setPaymentStatus('timeout'); return; }
    setPaymentChecking(true);
    try {
      const res = await axios.get(`${API_URL}/api/payments/checkout/status/${sessionId}`);
      if (res.data.payment_status === 'paid') {
        setPaymentStatus('success');
        refreshUser();
        fetchSubscription();
      } else if (res.data.status === 'expired') {
        setPaymentStatus('expired');
      } else {
        setTimeout(() => checkPaymentStatus(sessionId, attempts + 1), 2000);
        return;
      }
    } catch (error) {
      console.error('Payment status check error:', error);
    } finally {
      setPaymentChecking(false);
    }
  };

  const handleUpdateProfile = async () => {
    setLoading(true);
    try {
      await axios.put(`${API_URL}/api/settings/profile`, {
        full_name: profile.full_name,
        business_name: profile.business_name,
        phone: profile.phone,
        industry: profile.industry,
      });
      toast.success('Profile updated successfully');
      refreshUser();
    } catch (error) {
      toast.error('Failed to save profile');
      console.error('Update profile error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateBrandKit = async () => {
    setLoading(true);
    try {
      const res = await axios.put(`${API_URL}/api/settings/brand-kit`, brandKit);
      const saved = res.data?.brand_kit || brandKit;
      setBrandKit(prev => ({ ...prev, ...saved }));
      toast.success('Brand kit saved');
    } catch (error) {
      console.error('Update brand kit error:', error);
      toast.error(error.response?.data?.detail || 'Failed to save brand kit');
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async (planId) => {
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/payments/checkout`, {
        plan: planId,
        origin_url: window.location.origin,
      });
      if (res.data.url) window.location.href = res.data.url;
    } catch (error) {
      console.error('Checkout error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="settings-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-heading font-bold text-text-primary">Settings<PageInfoTooltip pageKey="settings" /></h1>
        <p className="text-gray-medium">Manage your account and preferences</p>
      </div>

      {paymentChecking && (
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4 flex items-center gap-3">
            <Loader2 className="h-5 w-5 text-blue-500 animate-spin" />
            <span className="text-blue-700">Checking payment status...</span>
          </CardContent>
        </Card>
      )}
      {paymentStatus === 'success' && (
        <Card className="bg-emerald-50 border-emerald-200">
          <CardContent className="p-4 flex items-center gap-3">
            <Check className="h-5 w-5 text-emerald-500" />
            <span className="text-emerald-700">Payment successful! Your plan has been upgraded.</span>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="profile" className="space-y-6">
        <TabsList className="overflow-x-auto flex-nowrap bg-white border border-gray-border flex-wrap h-auto gap-1 p-1">
          <TabsTrigger value="profile" className="gap-2"><User className="h-4 w-4" />Profile</TabsTrigger>
          <TabsTrigger value="brand-kit" className="gap-2"><Palette className="h-4 w-4" />Brand Kit</TabsTrigger>
          <TabsTrigger value="integrations" className="gap-2"><LinkIcon className="h-4 w-4" />Integrations</TabsTrigger>
          <TabsTrigger value="notifications" className="gap-2"><Bell className="h-4 w-4" />Notifications</TabsTrigger>
          {/* Billing tab is subscriber-only. Super Admin owns the platform (no
              subscription). Country Operators buy CREDITS from the platform,
              not tiered subscriptions — Billing is replaced by a Credits tab. */}
          {user?.role !== 'super_admin' && user?.role !== 'country_operator' && (
            <TabsTrigger value="billing" className="gap-2"><CreditCard className="h-4 w-4" />Billing</TabsTrigger>
          )}
          {user?.role === 'country_operator' && (
            <TabsTrigger value="credits" className="gap-2" data-testid="tab-credits"
              onClick={(e) => { e.preventDefault(); navigate('/settings/credits'); }}>
              <CreditCard className="h-4 w-4" />Credits
            </TabsTrigger>
          )}
          <TabsTrigger value="letterhead" className="gap-2"><FileText className="h-4 w-4" />Letterhead</TabsTrigger>
          <TabsTrigger value="help" className="gap-2"><HelpCircle className="h-4 w-4" />Help & Tours</TabsTrigger>
          <TabsTrigger value="danger" className="gap-2 text-red-700 data-[state=active]:text-red-700" data-testid="tab-danger-zone"><ShieldAlert className="h-4 w-4" />Danger Zone</TabsTrigger>
        </TabsList>

        <TabsContent value="profile">
          <ProfileTab profile={profile} setProfile={setProfile} user={user} loading={loading} onSave={handleUpdateProfile} />
        </TabsContent>

        <TabsContent value="brand-kit">
          <BrandKitTab brandKit={brandKit} setBrandKit={setBrandKit} loading={loading} onSave={handleUpdateBrandKit} />
        </TabsContent>

        <TabsContent value="integrations">
          <IntegrationsTab />
        </TabsContent>

        <TabsContent value="notifications">
          <NotificationsTab />
        </TabsContent>

        <TabsContent value="billing">
          {user?.role !== 'super_admin' && user?.role !== 'country_operator' && (
            <BillingTab user={user} subscription={subscription} loading={loading} onUpgrade={handleUpgrade} />
          )}
          {user?.role === 'country_operator' && (
            <div className="p-6 bg-emerald-50/40 border border-emerald-200 rounded-lg" data-testid="co-no-billing">
              <div className="flex items-start gap-3">
                <CreditCard className="h-5 w-5 text-emerald-700 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h3 className="font-heading font-semibold text-gray-900">Country Operators use Credits, not subscriptions</h3>
                  <p className="text-sm text-gray-700 mt-1">
                    You purchase credits from the Platform to service your subscribers. There is no monthly subscription for your role.
                    Choose a credit package or enable auto-replenish to keep your credit pool topped up automatically.
                  </p>
                  <button onClick={() => navigate('/settings/credits')}
                    className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700"
                    data-testid="co-go-to-credits">
                    Go to Top Up Credits →
                  </button>
                </div>
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="letterhead">
          <LetterheadSettings />
        </TabsContent>

        <TabsContent value="help">
          <HelpToursTab />
        </TabsContent>

        <TabsContent value="danger">
          <DangerZoneTab />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SettingsPage;
